import { Modal, PasswordInput, Button, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zodResolver } from "mantine-form-zod-resolver";
import { useMutation } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { usersApi } from "../../api/users.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
    opened: boolean;
    onClose: () => void;
    userId: number;
    userName: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function UserPasswordResetModal({ opened, onClose, userId, userName }: Props) {
    const { t } = useTranslation();
    const apiError = useApiErrorMessage();

    // Built inside the component (rather than at module scope, like every
    // other form's schema in this app) because its messages need `t()` —
    // the resolver still only runs on submit/blur, so rebuilding it each
    // render costs nothing observable.
    const ResetForm = z
        .object({
            newPassword: z.string().min(8, t("users.passwordReset.minLength")),
            confirm: z.string(),
        })
        .refine((v) => v.newPassword === v.confirm, {
            message: t("users.passwordReset.mismatch"),
            path: ["confirm"],
        });

    const form = useForm({
        initialValues: { newPassword: "", confirm: "" },
        validate: zodResolver(ResetForm),
    });

    const mut = useMutation({
        mutationFn: () => usersApi.resetPassword(userId, { newPassword: form.values.newPassword }),
        onSuccess: () => {
            notifications.show({ message: t("users.passwordReset.notify.success", { name: userName }), color: "teal" });
            form.reset();
            onClose();
        },
        onError: (e) => {
            notifications.show({ message: apiError(e, { fallback: "users.passwordReset.error.failed" }), color: "red" });
        },
    });

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t("users.passwordReset.title", { name: userName })}
        >
            <form onSubmit={form.onSubmit(() => mut.mutate())}>
                <Stack>
                    <PasswordInput
                        label={t("users.passwordReset.newPasswordLabel")}
                        required
                        {...form.getInputProps("newPassword")}
                    />
                    <PasswordInput
                        label={t("users.passwordReset.confirmLabel")}
                        required
                        {...form.getInputProps("confirm")}
                    />
                    <Button type="submit" loading={mut.isPending}>
                        {t("users.passwordReset.submitButton")}
                    </Button>
                </Stack>
            </form>
        </Modal>
    );
}
